/**
 * HTML Color Extractor Utility
 * Extracts colors from HTML content in various formats (hex, hexa, rgb, rgba)
 * from inline styles, CSS properties, and HTML attributes
 */

import { parseColorStringToHex } from '@client/components/ColorPicker/utils'

/**
 * Regular expressions for different color formats
 */
const COLOR_PATTERNS = {
  // Hex colors: #fff, #ffffff, #ffff, #ffffffff
  hex: /#([A-Fa-f0-9]{3,4}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})\b/g,
  
  // RGB colors: rgb(255, 255, 255)
  rgb: /rgb\s*\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*\)/gi,
  
  // RGBA colors: rgba(255, 255, 255, 0.5)
  rgba: /rgba\s*\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*,\s*(\d*\.?\d+)\s*\)/gi,
  
  // HSL colors: hsl(360, 100%, 50%)
  hsl: /hsl\s*\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)%\s*,\s*(\d+(?:\.\d+)?)%\s*\)/gi,
  
  // HSLA colors: hsla(360, 100%, 50%, 0.5)
  hsla: /hsla\s*\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)%\s*,\s*(\d+(?:\.\d+)?)%\s*,\s*(\d*\.?\d+)\s*\)/gi,
  
  // Named colors (basic set - can be extended)
  named: /\b(red|green|blue|yellow|orange|purple|pink|brown|black|white|gray|grey|cyan|magenta|lime|navy|olive|teal|silver|maroon|fuchsia|aqua)\b/gi
}

/**
 * HTML attributes that commonly contain colors
 */
const COLOR_ATTRIBUTES = [
  'bgcolor', 'color', 'bordercolor', 'background', 'text', 'link', 'vlink', 'alink'
]

/**
 * CSS properties that contain colors
 */
const COLOR_CSS_PROPERTIES = [
  'color', 'background-color', 'background', 'border-color', 'border-top-color',
  'border-right-color', 'border-bottom-color', 'border-left-color', 'outline-color',
  'text-decoration-color', 'text-shadow', 'box-shadow', 'fill', 'stroke',
  'stop-color', 'flood-color', 'lighting-color'
]

/**
 * Convert named colors to hex values
 */
const NAMED_COLORS = {
  red: '#ff0000', green: '#008000', blue: '#0000ff', yellow: '#ffff00',
  orange: '#ffa500', purple: '#800080', pink: '#ffc0cb', brown: '#a52a2a',
  black: '#000000', white: '#ffffff', gray: '#808080', grey: '#808080',
  cyan: '#00ffff', magenta: '#ff00ff', lime: '#00ff00', navy: '#000080',
  olive: '#808000', teal: '#008080', silver: '#c0c0c0', maroon: '#800000',
  fuchsia: '#ff00ff', aqua: '#00ffff'
}

/**
 * Extract all colors from a color string using all patterns
 * @param {string} text - Text to extract colors from
 * @returns {Array} Array of color objects with format and value
 */
function extractColorsFromText(text) {
  const colors = []
  
  if (!text || typeof text !== 'string') {
    return colors
  }

  // Extract hex colors
  let match
  const hexPattern = new RegExp(COLOR_PATTERNS.hex.source, 'gi')
  while ((match = hexPattern.exec(text)) !== null) {
    colors.push({
      format: 'hex',
      value: match[0],
      normalized: match[0].toLowerCase()
    })
  }

  // Extract RGB colors
  const rgbPattern = new RegExp(COLOR_PATTERNS.rgb.source, 'gi')
  while ((match = rgbPattern.exec(text)) !== null) {
    colors.push({
      format: 'rgb',
      value: match[0],
      normalized: parseColorStringToHex(match[0])
    })
  }

  // Extract RGBA colors
  const rgbaPattern = new RegExp(COLOR_PATTERNS.rgba.source, 'gi')
  while ((match = rgbaPattern.exec(text)) !== null) {
    colors.push({
      format: 'rgba',
      value: match[0],
      normalized: parseColorStringToHex(match[0])
    })
  }

  // Extract HSL colors
  const hslPattern = new RegExp(COLOR_PATTERNS.hsl.source, 'gi')
  while ((match = hslPattern.exec(text)) !== null) {
    colors.push({
      format: 'hsl',
      value: match[0],
      normalized: hslToHex(parseInt(match[1]), parseInt(match[2]), parseInt(match[3]))
    })
  }

  // Extract HSLA colors
  const hslaPattern = new RegExp(COLOR_PATTERNS.hsla.source, 'gi')
  while ((match = hslaPattern.exec(text)) !== null) {
    colors.push({
      format: 'hsla',
      value: match[0],
      normalized: hslToHex(parseInt(match[1]), parseInt(match[2]), parseInt(match[3]))
    })
  }

  // Extract named colors
  const namedPattern = new RegExp(COLOR_PATTERNS.named.source, 'gi')
  while ((match = namedPattern.exec(text)) !== null) {
    const colorName = match[0].toLowerCase()
    if (NAMED_COLORS[colorName]) {
      colors.push({
        format: 'named',
        value: match[0],
        normalized: NAMED_COLORS[colorName]
      })
    }
  }

  return colors
}

/**
 * Convert HSL to HEX
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} Hex color
 */
function hslToHex(h, s, l) {
  l /= 100
  const a = s * Math.min(l, 1 - l) / 100
  const f = n => {
    const k = (n + h / 30) % 12
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
    return Math.round(255 * color).toString(16).padStart(2, '0')
  }
  return `#${f(0)}${f(8)}${f(4)}`
}

/**
 * Extract colors from HTML element's style attribute
 * @param {Element|string} element - DOM element or HTML string
 * @returns {Array} Array of color objects
 */
export function extractColorsFromStyles(element) {
  const colors = []
  
  if (typeof element === 'string') {
    // Parse HTML string
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = element
    const elements = tempDiv.querySelectorAll('*')
    
    elements.forEach(el => {
      const style = el.getAttribute('style')
      if (style) {
        const styleColors = extractColorsFromText(style)
        styleColors.forEach(color => {
          colors.push({
            ...color,
            source: 'inline-style',
            element: el.tagName.toLowerCase(),
            property: getColorProperty(style, color.value)
          })
        })
      }
    })
  } else if (element && element.nodeType === Node.ELEMENT_NODE) {
    // Single DOM element
    const style = element.getAttribute('style')
    if (style) {
      const styleColors = extractColorsFromText(style)
      styleColors.forEach(color => {
        colors.push({
          ...color,
          source: 'inline-style',
          element: element.tagName.toLowerCase(),
          property: getColorProperty(style, color.value)
        })
      })
    }
  }
  
  return colors
}

/**
 * Extract colors from HTML element attributes
 * @param {Element|string} element - DOM element or HTML string
 * @returns {Array} Array of color objects
 */
export function extractColorsFromAttributes(element) {
  const colors = []
  
  if (typeof element === 'string') {
    // Parse HTML string
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = element
    const elements = tempDiv.querySelectorAll('*')
    
    elements.forEach(el => {
      COLOR_ATTRIBUTES.forEach(attr => {
        const value = el.getAttribute(attr)
        if (value) {
          const attrColors = extractColorsFromText(value)
          attrColors.forEach(color => {
            colors.push({
              ...color,
              source: 'attribute',
              element: el.tagName.toLowerCase(),
              attribute: attr
            })
          })
        }
      })
    })
  } else if (element && element.nodeType === Node.ELEMENT_NODE) {
    // Single DOM element
    COLOR_ATTRIBUTES.forEach(attr => {
      const value = element.getAttribute(attr)
      if (value) {
        const attrColors = extractColorsFromText(value)
        attrColors.forEach(color => {
          colors.push({
            ...color,
            source: 'attribute',
            element: element.tagName.toLowerCase(),
            attribute: attr
          })
        })
      }
    })
  }
  
  return colors
}

/**
 * Extract colors from computed styles of DOM elements
 * @param {Element|string} element - DOM element or HTML string
 * @returns {Array} Array of color objects
 */
export function extractColorsFromComputedStyles(element) {
  const colors = []
  
  if (typeof element === 'string') {
    // For HTML strings, we need to temporarily add to DOM to get computed styles
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = element
    document.body.appendChild(tempDiv)
    
    try {
      const elements = tempDiv.querySelectorAll('*')
      elements.forEach(el => {
        const computedStyle = window.getComputedStyle(el)
        COLOR_CSS_PROPERTIES.forEach(prop => {
          const value = computedStyle.getPropertyValue(prop)
          if (value && value !== 'initial' && value !== 'inherit') {
            const propColors = extractColorsFromText(value)
            propColors.forEach(color => {
              colors.push({
                ...color,
                source: 'computed-style',
                element: el.tagName.toLowerCase(),
                property: prop
              })
            })
          }
        })
      })
    } finally {
      document.body.removeChild(tempDiv)
    }
  } else if (element && element.nodeType === Node.ELEMENT_NODE) {
    // Single DOM element
    const computedStyle = window.getComputedStyle(element)
    COLOR_CSS_PROPERTIES.forEach(prop => {
      const value = computedStyle.getPropertyValue(prop)
      if (value && value !== 'initial' && value !== 'inherit') {
        const propColors = extractColorsFromText(value)
        propColors.forEach(color => {
          colors.push({
            ...color,
            source: 'computed-style',
            element: element.tagName.toLowerCase(),
            property: prop
          })
        })
      }
    })
  }
  
  return colors
}

/**
 * Get the CSS property name that contains a specific color value
 * @param {string} styleText - CSS style text
 * @param {string} colorValue - Color value to find
 * @returns {string|null} Property name or null
 */
function getColorProperty(styleText, colorValue) {
  const properties = styleText.split(';')
  for (const prop of properties) {
    if (prop.includes(colorValue)) {
      const [property] = prop.split(':')
      return property.trim()
    }
  }
  return null
}

/**
 * Main function to extract all colors from HTML
 * @param {Element|string} input - DOM element or HTML string
 * @param {Object} options - Extraction options
 * @param {boolean} options.includeInlineStyles - Extract from style attributes (default: true)
 * @param {boolean} options.includeAttributes - Extract from HTML attributes (default: true)
 * @param {boolean} options.includeComputedStyles - Extract from computed styles (default: false)
 * @param {boolean} options.unique - Return unique colors only (default: true)
 * @param {string} options.format - Return format: 'all', 'hex', 'normalized' (default: 'all')
 * @returns {Array} Array of color objects or color strings
 */
export function extractColorsFromHTML(input, options = {}) {
  const {
    includeInlineStyles = true,
    includeAttributes = true,
    includeComputedStyles = false,
    unique = true,
    format = 'all'
  } = options

  let allColors = []

  // Extract from inline styles
  if (includeInlineStyles) {
    allColors = allColors.concat(extractColorsFromStyles(input))
  }

  // Extract from HTML attributes
  if (includeAttributes) {
    allColors = allColors.concat(extractColorsFromAttributes(input))
  }

  // Extract from computed styles (only for DOM elements)
  if (includeComputedStyles && typeof input !== 'string') {
    allColors = allColors.concat(extractColorsFromComputedStyles(input))
  }

  // Remove duplicates if requested
  if (unique) {
    const seen = new Set()
    allColors = allColors.filter(color => {
      const key = format === 'normalized' ? color.normalized : color.value
      if (seen.has(key)) {
        return false
      }
      seen.add(key)
      return true
    })
  }

  // Format output
  switch (format) {
    case 'hex':
    case 'normalized':
      return allColors.map(color => color.normalized)
    case 'values':
      return allColors.map(color => color.value)
    default:
      return allColors
  }
}

/**
 * Extract only unique color values in hex format
 * @param {Element|string} input - DOM element or HTML string
 * @param {Object} options - Extraction options
 * @returns {Array<string>} Array of unique hex color strings
 */
export function extractUniqueColors(input, options = {}) {
  return extractColorsFromHTML(input, { ...options, unique: true, format: 'normalized' })
}

/**
 * Count colors by format
 * @param {Element|string} input - DOM element or HTML string
 * @param {Object} options - Extraction options
 * @returns {Object} Object with counts by format
 */
export function getColorStatistics(input, options = {}) {
  const colors = extractColorsFromHTML(input, { ...options, unique: false })
  const stats = {
    total: colors.length,
    formats: {},
    sources: {},
    unique: extractColorsFromHTML(input, { ...options, unique: true }).length
  }

  colors.forEach(color => {
    stats.formats[color.format] = (stats.formats[color.format] || 0) + 1
    stats.sources[color.source] = (stats.sources[color.source] || 0) + 1
  })

  return stats
}

export default extractColorsFromHTML
